import React, { useEffect, useState, useCallback } from 'react'
import { supabase } from '../lib/supabase'

const fmt  = n => '₦' + (Number(n)||0).toLocaleString(undefined, { maximumFractionDigits: 0 })
const fmtM = n => { const v = (Number(n)||0)/1000000; return '₦' + v.toLocaleString(undefined,{maximumFractionDigits:1}) + 'M' }

const T = {
  bg:          '#0d1117',
  surface:     '#161b22',
  surface2:    '#1c2333',
  surface3:    '#21262d',
  border:      '#30363d',
  border2:     '#444c56',
  txtPrimary:  '#e6edf3',
  txtSecondary:'#8b949e',
  txtMuted:    '#484f58',
  blue:        '#1E90FF',
  blueGlow:    'rgba(30,144,255,.15)',
  amber:       '#F5A623',
  green:       '#3fb950',
  orange:      '#f0883e',
  red:         '#f85149',
  font:        "'Montserrat', sans-serif",
}

const TIER_CFG = {
  A: { label:'Tier A', desc:'Urban Core LGAs',       colour:'#1E90FF', bg:'rgba(30,144,255,.12)',  border:'rgba(30,144,255,.25)' },
  B: { label:'Tier B', desc:'Secondary Market LGAs', colour:'#F5A623', bg:'rgba(245,166,35,.12)',  border:'rgba(245,166,35,.25)' },
  C: { label:'Tier C', desc:'Rural LGAs',            colour:'#3fb950', bg:'rgba(63,185,80,.12)',   border:'rgba(63,185,80,.25)'  },
  D: { label:'Tier D', desc:'Outlying LGAs',         colour:'#8b949e', bg:'rgba(139,148,158,.10)', border:'rgba(139,148,158,.2)' },
}

const CLASS_CFG = {
  '1':{ label:'Class 1', colour:'#1E90FF' },
  '2':{ label:'Class 2', colour:'#F5A623' },
  '3':{ label:'Class 3', colour:'#8b949e' },
}

const ZONE_CFG = {
  'North':  { colour:'#58aeff', bg:'rgba(88,174,255,.12)',  border:'rgba(88,174,255,.25)' },
  'South':  { colour:'#3fb950', bg:'rgba(63,185,80,.12)',   border:'rgba(63,185,80,.25)'  },
  'Central':{ colour:'#F5A623', bg:'rgba(245,166,35,.12)',  border:'rgba(245,166,35,.25)' },
}

// Mirrors TargetSettings.formulaTarget exactly
function formulaTarget(sa, targets, season) {
  const seasonFactor = season === 'rain' ? 0.75 : 1.0
  const secFactor    = (sa.lgas?.security_level || '') === 'Red' ? 0.75 : 1.0
  const tier         = sa.lgas?.tier || 'A'
  const agentList    = sa.agents || []
  if (agentList.length === 0) return 0
  const cls  = agentList[0]?.agent_class || '1'
  const base = (targets[tier]?.[cls] || targets['A']?.[cls] || 120000) * seasonFactor * secFactor
  return Math.round(base * agentList.length)
}

function LgaRow({ lgaName, saList, zone, tier }) {
  const [open, setOpen] = useState(false)
  const tc  = TIER_CFG[tier] || TIER_CFG.A
  const zc  = ZONE_CFG[zone] || { colour:T.txtSecondary, bg:T.surface2, border:T.border }
  const total = saList.reduce((s,a) => s + a.target, 0)

  return (
    <div style={{
      border:`1px solid ${open ? tc.border : T.border}`,
      borderRadius:10, marginBottom:8,
      background:open ? T.surface : 'transparent',
      transition:'all .15s', overflow:'hidden',
    }}>
      <div onClick={() => setOpen(o => !o)} style={{
        display:'flex', alignItems:'center', gap:12,
        padding:'14px 18px', cursor:'pointer',
        background:open ? T.surface2 : T.surface, userSelect:'none',
      }}>
        <span style={{
          fontSize:10, color:T.txtMuted, width:14, flexShrink:0,
          transform:open ? 'rotate(90deg)' : 'rotate(0deg)', transition:'transform .15s',
        }}>▶</span>

        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:13, fontWeight:700, color:T.txtPrimary, lineHeight:1.3 }}>
            {lgaName} LGA
            {saList.some(a => a.isDanger) && (
              <span style={{
                marginLeft:8, fontSize:9, fontWeight:700,
                color:T.red, background:'rgba(248,81,73,.12)', border:'1px solid rgba(248,81,73,.3)',
                borderRadius:4, padding:'1px 5px', verticalAlign:'middle',
              }}>⚠ DANGER</span>
            )}
          </div>
          <div style={{ fontSize:11, color:T.txtSecondary, marginTop:2 }}>
            {saList.length} Super Agent{saList.length !== 1 ? 's' : ''}
          </div>
        </div>

        {zone && zone !== '—' && (
          <span style={{
            fontSize:9, fontWeight:700, letterSpacing:'.08em', textTransform:'uppercase',
            color:zc.colour, background:zc.bg, border:`1px solid ${zc.border}`,
            borderRadius:20, padding:'3px 9px', flexShrink:0,
          }}>{zone}</span>
        )}

        <span style={{
          fontSize:9, fontWeight:700, letterSpacing:'.06em', textTransform:'uppercase',
          color:tc.colour, background:tc.bg, border:`1px solid ${tc.border}`,
          borderRadius:20, padding:'3px 9px', flexShrink:0,
        }}>{tier}</span>

        <div style={{ textAlign:'right', flexShrink:0 }}>
          <div style={{ fontSize:16, fontWeight:800, color:T.green, letterSpacing:'-.02em' }}>
            {fmtM(total)}
          </div>
          <div style={{ fontSize:9, color:T.txtMuted, textTransform:'uppercase', letterSpacing:'.06em' }}>LGA total</div>
        </div>
      </div>

      {open && (
        <div style={{ padding:'0 18px 16px' }}>
          <div style={{
            display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(260px, 1fr))',
            gap:10, marginTop:14,
          }}>
            {saList.map(sa => {
              const cls = sa.agents?.[0]?.agent_class
              const cc  = CLASS_CFG[cls] || null
              return (
                <div key={sa.id} style={{
                  background:sa.isDanger ? 'rgba(248,81,73,.05)' : T.surface3,
                  border:`1px solid ${sa.isDanger ? 'rgba(248,81,73,.25)' : T.border}`,
                  borderRadius:8, padding:'14px 16px',
                  display:'flex', flexDirection:'column', gap:8,
                }}>
                  <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:8 }}>
                    <div style={{ fontSize:12, fontWeight:700, color:T.txtPrimary, lineHeight:1.35, flex:1, minWidth:0 }}>
                      {sa.company_name}
                    </div>
                    {cc && (
                      <span style={{
                        fontSize:9, fontWeight:700,
                        color:cc.colour, background:T.surface2, border:`1px solid ${T.border}`,
                        borderRadius:4, padding:'2px 7px', flexShrink:0,
                      }}>{cc.label}</span>
                    )}
                  </div>

                  <div style={{ height:1, background:T.border }} />

                  <div style={{ display:'flex', alignItems:'baseline', justifyContent:'space-between', gap:8 }}>
                    <span style={{
                      fontSize:10, fontWeight:600, color:T.txtSecondary,
                      letterSpacing:'.06em', textTransform:'uppercase',
                    }}>
                      Weekly Target
                      {sa.isDanger && !sa.isOverride && <span style={{ color:T.orange }}> (−25%)</span>}
                    </span>
                    <span style={{
                      fontSize:20, fontWeight:800, letterSpacing:'-.03em',
                      color: sa.target === 0 ? T.txtMuted : sa.isDanger ? T.orange : T.green,
                      fontVariantNumeric:'tabular-nums',
                    }}>
                      {sa.target === 0 ? 'No agents' : fmt(sa.target)}
                    </span>
                  </div>

                  {sa.isOverride && (
                    <div style={{ fontSize:10, color:T.amber, display:'flex', alignItems:'center', gap:4 }}>
                      <span style={{ fontSize:6 }}>●</span> Custom target set by ACSM
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

export default function PublicTargets() {
  const [superAgents, setSuperAgents] = useState([])
  const [overrides,   setOverrides]   = useState([])
  const [targets,     setTargets]     = useState({})
  const [season,      setSeason]      = useState('dry')
  const [loading,     setLoading]     = useState(true)
  const [dbError,     setDbError]     = useState(null)
  const [lastRefresh, setLastRefresh] = useState('')
  const [filterTier,  setFilterTier]  = useState('')
  const [filterZone,  setFilterZone]  = useState('')
  const [filterClass, setFilterClass] = useState('')
  const [acsmLogo,    setAcsmLogo]    = useState(null)
  const [hasZone,     setHasZone]     = useState(true)   // optimistic; flips if zone column missing

  useEffect(() => {
    const img = new Image()
    img.onload  = () => setAcsmLogo('/acsm-logo.png')
    img.onerror = () => setAcsmLogo(null)
    img.src = '/acsm-logo.png'
  }, [])

  const load = useCallback(async () => {
    // Try with zone first; if it errors, retry without
    let saResult = await supabase.from('super_agents')
      .select('id, company_name, status, lgas(name, tier, zone, security_level), agents!agents_super_agent_id_fkey(id, agent_class)')
      .eq('status', 'Active')
      .order('company_name')

    if (saResult.error) {
      console.warn('PublicTargets: zone column may not exist, retrying without it:', saResult.error.message)
      setHasZone(false)
      saResult = await supabase.from('super_agents')
        .select('id, company_name, status, lgas(name, tier, security_level), agents!agents_super_agent_id_fkey(id, agent_class)')
        .eq('status', 'Active')
        .order('company_name')
    }

    const [
      { data:ov, error:ovErr },
      { data:tg, error:tgErr },
      { data:se, error:seErr },
    ] = await Promise.all([
      supabase.from('target_overrides').select('*').is('cleared_at', null),
      supabase.from('weekly_targets').select('*').order('effective_from', { ascending:false }).limit(10),
      supabase.from('app_settings').select('*'),
    ])

    if (saResult.error) {
      console.error('PublicTargets super_agents error:', saResult.error)
      setDbError(saResult.error.message)
      setLoading(false)
      return
    }

    console.log('PublicTargets loaded:', {
      superAgents: saResult.data?.length,
      overrides: ov?.length,
      targets: tg?.length,
      tgErr, ovErr, seErr,
      sample: saResult.data?.[0],
    })

    const tm = {}
    ;(tg || []).forEach(t => {
      if (!t.tier) return
      if (!tm[t.tier]) tm[t.tier] = {}
      if (!tm[t.tier][t.agent_class]) tm[t.tier][t.agent_class] = t.dry_season_target
    })

    const s = (se || []).find(x => x.key === 'season')
    setSeason(s?.value || 'dry')
    setTargets(tm)
    setSuperAgents(saResult.data || [])
    setOverrides(ov || [])
    setDbError(null)
    setLastRefresh(new Date().toLocaleTimeString('en-GB', { hour:'2-digit', minute:'2-digit' }))
    setLoading(false)
  }, [])

  useEffect(() => {
    load()
    const interval = setInterval(load, 5 * 60 * 1000)
    return () => clearInterval(interval)
  }, [load])

  const rows = superAgents.map(sa => {
    const ov     = overrides.find(o => o.super_agent_id === sa.id)
    const target = ov ? ov.override_amount : formulaTarget(sa, targets, season)
    const cls    = sa.agents?.[0]?.agent_class || null
    return {
      ...sa,
      lgaName:    sa.lgas?.name || '—',
      tier:       sa.lgas?.tier || 'A',
      zone:       sa.lgas?.zone || null,
      isDanger:   (sa.lgas?.security_level || '') === 'Red',
      target:     Number(target) || 0,
      isOverride: !!ov,
      agentClass: cls,
    }
  })

  const filtered = rows.filter(r =>
    (!filterTier  || r.tier === filterTier) &&
    (!filterZone  || (r.zone||'').toLowerCase() === filterZone.toLowerCase()) &&
    (!filterClass || r.agentClass === filterClass)
  )

  // Group: LGA name → { tier, zone, saList[] }
  const lgaMap = {}
  filtered.forEach(r => {
    const key = r.lgaName
    if (!lgaMap[key]) lgaMap[key] = { tier:r.tier, zone:r.zone, saList:[] }
    lgaMap[key].saList.push(r)
  })

  // Group: tier → zone (or 'All') → [lga entries]
  const grouped = {}
  Object.entries(lgaMap).forEach(([lgaName, { tier, zone, saList }]) => {
    const z = zone || 'Unzoned'
    if (!grouped[tier]) grouped[tier] = {}
    if (!grouped[tier][z]) grouped[tier][z] = []
    grouped[tier][z].push({ lgaName, tier, zone, saList })
  })

  const tiers     = ['A','B','C','D'].filter(t => grouped[t])
  const allZones  = [...new Set(rows.map(r => r.zone).filter(Boolean))].sort()
  const allTiers  = [...new Set(rows.map(r => r.tier))].sort()
  const grandTotal = filtered.reduce((s,r) => s + r.target, 0)
  const saCount    = filtered.length
  const lgaCount   = Object.keys(lgaMap).length
  const showZoneFilter = hasZone && allZones.length > 0

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', fontFamily:T.font, color:T.txtSecondary, fontSize:13, background:T.bg }}>
      Loading targets…
    </div>
  )

  if (dbError) return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'100vh', fontFamily:T.font, color:T.red, fontSize:13, background:T.bg, gap:8 }}>
      <div style={{ fontWeight:700 }}>Failed to load targets</div>
      <div style={{ color:T.txtSecondary, fontSize:11 }}>{dbError}</div>
    </div>
  )

  return (
    <div style={{ fontFamily:T.font, background:T.bg, minHeight:'100vh', color:T.txtPrimary }}>
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700;800;900&display=swap" />

      {/* Header */}
      <div style={{ background:T.surface, borderBottom:`1px solid ${T.border}`, position:'sticky', top:0, zIndex:100 }}>
        <div style={{
          maxWidth:1100, margin:'0 auto', padding:'14px 24px',
          display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, flexWrap:'wrap',
        }}>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            {acsmLogo
              ? <img src={acsmLogo} alt="ACSM" style={{ height:38, width:'auto', objectFit:'contain' }} />
              : <div style={{
                  width:38, height:38, borderRadius:8, background:T.blueGlow,
                  border:'1px solid rgba(30,144,255,.3)', display:'flex',
                  alignItems:'center', justifyContent:'center', fontWeight:900, fontSize:12, color:T.blue,
                }}>AC</div>
            }
            <div>
              <div style={{ fontSize:14, fontWeight:800, color:T.txtPrimary, letterSpacing:'-.01em', lineHeight:1.2 }}>
                Agent Compliance Scoring &amp; Monitoring
              </div>
              <div style={{ fontSize:10, color:T.txtSecondary, letterSpacing:'.04em', marginTop:1 }}>
                KATSINA STATE INTERNAL REVENUE SERVICE · KTIRS
              </div>
            </div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            {lastRefresh && <span style={{ fontSize:10, color:T.txtMuted }}>↻ {lastRefresh}</span>}
            <span style={{
              fontSize:9, fontWeight:700, letterSpacing:'.1em', textTransform:'uppercase',
              color:T.txtMuted, border:`1px solid ${T.border}`, borderRadius:20, padding:'3px 10px',
            }}>Public · Read Only</span>
            <a href="/login" style={{
              fontSize:11, fontWeight:700, color:'#E8631A',
              background:'rgba(232,99,26,.12)', border:'1px solid rgba(232,99,26,.35)',
              borderRadius:6, padding:'6px 12px', textDecoration:'none', fontFamily:T.font,
            }}>← Officer Login</a>
          </div>
        </div>
      </div>

      <div style={{ maxWidth:1100, margin:'0 auto', padding:'0 24px 60px' }}>

        {/* Title */}
        <div style={{ paddingTop:28, marginBottom:20 }}>
          <h1 style={{ fontSize:'clamp(20px,3vw,28px)', fontWeight:900, color:T.txtPrimary, letterSpacing:'-.02em', marginBottom:6 }}>
            Super Agent Weekly Targets
          </h1>
          <p style={{ fontSize:12, color:T.txtSecondary, marginBottom:16 }}>
            Live targets for all active Super Agents across Katsina State · auto-refreshes every 5 minutes
          </p>
          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            <span style={{
              padding:'5px 12px', borderRadius:20, fontSize:11, fontWeight:600,
              color:season === 'rain' ? '#64ADEF' : '#F5C842',
              background:season === 'rain' ? 'rgba(100,173,239,.1)' : 'rgba(245,200,66,.10)',
              border:`1px solid ${season === 'rain' ? 'rgba(100,173,239,.25)' : 'rgba(245,200,66,.25)'}`,
            }}>
              {season === 'rain' ? '🌧 Rainy Season — Targets −25%' : '☀ Dry Season — Full Targets'}
            </span>
            <span style={{
              padding:'5px 12px', borderRadius:20, fontSize:11, fontWeight:600,
              color:T.txtSecondary, background:'rgba(255,255,255,.04)', border:`1px solid ${T.border}`,
            }}>
              {new Date().toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' })}
            </span>
          </div>
        </div>

        {/* KPI bar */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:10, marginBottom:24 }}>
          {[
            { label:'Super Agents',        value:saCount,          colour:T.blue  },
            { label:'LGAs Covered',        value:lgaCount,         colour:T.amber },
            { label:'Total Weekly Target', value:fmtM(grandTotal), colour:T.green },
          ].map(({ label, value, colour }) => (
            <div key={label} style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:10, padding:'14px 18px' }}>
              <div style={{ fontSize:10, fontWeight:600, color:T.txtMuted, textTransform:'uppercase', letterSpacing:'.07em', marginBottom:6 }}>{label}</div>
              <div style={{ fontSize:22, fontWeight:800, color:colour, letterSpacing:'-.02em' }}>{value}</div>
            </div>
          ))}
        </div>

        {/* Filter bar */}
        <div style={{
          display:'flex', gap:10, flexWrap:'wrap', alignItems:'center',
          background:T.surface, border:`1px solid ${T.border}`, borderRadius:10,
          padding:'12px 18px', marginBottom:24,
        }}>
          <span style={{ fontSize:11, fontWeight:700, color:T.txtMuted, textTransform:'uppercase', letterSpacing:'.07em', marginRight:4 }}>Filter:</span>

          {showZoneFilter && ['', ...allZones].map(z => {
            const active = filterZone === z
            const zc = ZONE_CFG[z] || null
            return (
              <button key={z||'__z'} onClick={() => setFilterZone(z)} style={{
                padding:'5px 12px', borderRadius:20, fontSize:11, fontWeight:700, cursor:'pointer', border:'1px solid',
                background:active ? (zc ? zc.bg : T.blueGlow) : 'transparent',
                color:active ? (zc ? zc.colour : T.blue) : T.txtSecondary,
                borderColor:active ? (zc ? zc.border : 'rgba(30,144,255,.35)') : T.border,
                fontFamily:T.font,
              }}>{z || 'All Zones'}</button>
            )
          })}

          {showZoneFilter && <div style={{ width:1, height:18, background:T.border, margin:'0 4px' }} />}

          {['', ...allTiers].map(t => {
            const active = filterTier === t
            const tc = TIER_CFG[t] || null
            return (
              <button key={t||'__t'} onClick={() => setFilterTier(t)} style={{
                padding:'5px 12px', borderRadius:20, fontSize:11, fontWeight:700, cursor:'pointer', border:'1px solid',
                background:active ? (tc ? tc.bg : T.blueGlow) : 'transparent',
                color:active ? (tc ? tc.colour : T.blue) : T.txtSecondary,
                borderColor:active ? (tc ? tc.border : 'rgba(30,144,255,.35)') : T.border,
                fontFamily:T.font,
              }}>{t ? `Tier ${t}` : 'All Tiers'}</button>
            )
          })}

          <div style={{ width:1, height:18, background:T.border, margin:'0 4px' }} />

          {['', '1', '2', '3'].map(c => {
            const active = filterClass === c
            const cc = CLASS_CFG[c] || null
            return (
              <button key={c||'__c'} onClick={() => setFilterClass(c)} style={{
                padding:'5px 12px', borderRadius:20, fontSize:11, fontWeight:700, cursor:'pointer', border:'1px solid',
                background:active ? (cc ? `${cc.colour}1e` : T.blueGlow) : 'transparent',
                color:active ? (cc ? cc.colour : T.blue) : T.txtSecondary,
                borderColor:active ? T.border2 : T.border,
                fontFamily:T.font,
              }}>{c ? `Class ${c}` : 'All Classes'}</button>
            )
          })}

          <span style={{ marginLeft:'auto', fontSize:11, color:T.txtMuted }}>
            {saCount} SA · {lgaCount} LGA{lgaCount !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Tier sections */}
        {tiers.length === 0 && (
          <div style={{ padding:48, textAlign:'center', color:T.txtMuted, fontSize:13 }}>
            No Super Agents match the selected filters.
          </div>
        )}

        {tiers.map(tier => {
          const tc          = TIER_CFG[tier] || TIER_CFG.A
          const zonesInTier = Object.keys(grouped[tier]).sort()
          const allSaInTier = Object.values(grouped[tier]).flat().flatMap(l => l.saList)
          const tierTotal   = allSaInTier.reduce((s,a) => s + a.target, 0)
          const tierSaCount = allSaInTier.length

          return (
            <div key={tier} style={{ marginBottom:40 }}>
              <div style={{
                display:'flex', alignItems:'center', gap:12,
                paddingBottom:14, borderBottom:`1px solid ${tc.border}`, marginBottom:16,
              }}>
                <div style={{
                  width:36, height:36, borderRadius:9, flexShrink:0,
                  display:'flex', alignItems:'center', justifyContent:'center',
                  fontSize:16, fontWeight:900,
                  background:tc.bg, color:tc.colour, border:`1px solid ${tc.border}`,
                }}>{tier}</div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:15, fontWeight:800, color:T.txtPrimary, letterSpacing:'-.01em' }}>
                    {tc.label} — {tc.desc}
                  </div>
                  <div style={{ fontSize:11, color:T.txtSecondary, marginTop:2 }}>
                    {tierSaCount} Super Agent{tierSaCount !== 1 ? 's' : ''}
                    {zonesInTier.filter(z => z !== 'Unzoned').length > 0 &&
                      ` across ${zonesInTier.length} zone${zonesInTier.length !== 1 ? 's' : ''}`}
                  </div>
                </div>
                <div style={{ textAlign:'right', flexShrink:0 }}>
                  <div style={{ fontSize:18, fontWeight:800, color:tc.colour, letterSpacing:'-.02em' }}>{fmtM(tierTotal)}</div>
                  <div style={{ fontSize:9, color:T.txtMuted, textTransform:'uppercase', letterSpacing:'.06em' }}>tier total</div>
                </div>
              </div>

              {zonesInTier.map(zone => {
                const lgasInZone = grouped[tier][zone].sort((a,b) => a.lgaName.localeCompare(b.lgaName))
                const zc         = ZONE_CFG[zone] || { colour:T.txtSecondary, bg:T.surface2, border:T.border }
                const zoneTotal  = lgasInZone.flatMap(l => l.saList).reduce((s,a) => s + a.target, 0)
                const showZoneHeader = zone !== 'Unzoned'

                return (
                  <div key={zone} style={{ marginBottom:20 }}>
                    {showZoneHeader && (
                      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
                        <span style={{
                          fontSize:10, fontWeight:700, letterSpacing:'.1em', textTransform:'uppercase',
                          color:zc.colour, background:zc.bg, border:`1px solid ${zc.border}`,
                          borderRadius:20, padding:'3px 10px',
                        }}>{zone} Zone</span>
                        <div style={{ flex:1, height:1, background:T.border }} />
                        <span style={{ fontSize:11, color:T.txtMuted, fontWeight:600 }}>{fmtM(zoneTotal)}</span>
                      </div>
                    )}
                    {lgasInZone.map(({ lgaName, tier:t, zone:z, saList }) => (
                      <LgaRow key={lgaName} lgaName={lgaName} saList={saList} zone={z} tier={t} />
                    ))}
                  </div>
                )
              })}
            </div>
          )
        })}

        {/* Footer */}
        <div style={{
          display:'flex', alignItems:'center', justifyContent:'space-between',
          flexWrap:'wrap', gap:8, paddingTop:20, borderTop:`1px solid ${T.border}`, marginTop:8,
        }}>
          <span style={{ fontSize:11, color:T.txtMuted }}>
            Auto-refreshes every 5 minutes · Last refresh {lastRefresh}
          </span>
          <span style={{ fontSize:11, fontWeight:700, color:T.txtMuted, letterSpacing:'.04em' }}>
            ACSM · Katsina State KTIRS · Powered by Evolucionar Consultants
          </span>
        </div>
      </div>
    </div>
  )
}
